import React, { useEffect } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { getAccessAssistants, getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import WithError from '../../../components/with-error/WithError';
import { Input } from '../../../components/common-inputs';
import SearchInput from '../../../components/common-inputs/SearchInput';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import CommonButton from '../../../components/common-button/CommonButton';
import CommonSwitch from '../../../components/common-switch/CommonSwitch';
import Id8AgentFields from './Id8AgentFields';

import scss from './Id8Creation.module.scss';

function Id8CreationForm({ handleSubmit }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { restart, change } = useForm();
  const { values } = useFormState();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const isLoading = useSelector((state) => state.projects.isLoading);

  const { accessUsers, totalAccessUsers } = useSelector((state) => state.projects);

  const inactiveUsers = accessUsers.filter(
    (user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id),
  );

  const handleRestForm = () => {
    restart();
    navigate('/projects/create');
  };

  const handleActivateUser = (user) => {
    change('activeUsers', [...values.activeUsers, user]);
  };

  const handleInactivateUser = ({ id }) => {
    change(
      'activeUsers',
      values.activeUsers.filter((user) => user.id !== id),
    );
  };

  const handleSearchUsers = (newSearch) => {
    change('assignUser', newSearch);
    dispatch(getAccessUsers({ search: newSearch, page: 1 }));
  };

  const handleLoadMoreUsers = (newPage) => {
    dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
  };

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    dispatch(getAccessAssistants());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('projectName')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='project-name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='thinking' autoComplete='id8-thinking' type='checkbox'>
        {({ input }) => (
          <WithLabel variant={LABEL_STYLES.CUSTOM} label={`${t('thinking')}:`} isBold extraClassName={scss.inputRow}>
            <CommonSwitch checked={input.checked} onChange={input.onChange} />
          </WithLabel>
        )}
      </Field>

      <Id8AgentFields isLoading={isLoading} />

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.CUSTOM} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactivateUser}
              onActive={handleActivateUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton
          type='submit'
          styleColor='primary'
          onClick={handleSubmit}
          maxWidth={100}
          isLoading={isLoading}
          disabled={isLoading}>
          {t('create')}
        </CommonButton>
        <CommonButton type='button' styleColor='secondary' onClick={handleRestForm} maxWidth={100} disabled={isLoading}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
}

export default Id8CreationForm;
